import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormControlLabel,
} from "@mui/material";
import { Download, FileDownload } from "@mui/icons-material";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

function ProductExport({ open, onClose, categories }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportResults, setExportResults] = useState(null);

  const formatKeywords = (keywords) => {
    if (Array.isArray(keywords)) {
      return keywords.join(";");
    }
    return "";
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId || !categories) return "";
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : "";
  };

  const exportToCSV = (products) => {
    const headers = [
      "id",
      "name",
      "description",
      "price",
      "aisle",
      "rack_level",
      "category_id",
      "category_name",
      "barcode",
      "keywords",
      "image_url",
      "image_path"
    ];

    const csvContent = [
      headers.join(","),
      ...products.map(product => {
        const row = [
          product.id || "",
          `"${(product.name || "").replace(/"/g, '""')}"`,
          `"${(product.description || "").replace(/"/g, '""')}"`,
          product.price || 0,
          product.aisle || 0,
          product.rack_level || 0,
          product.category_id || "",
          `"${getCategoryName(product.category_id).replace(/"/g, '""')}"`,
          product.barcode || "",
          `"${formatKeywords(product.keywords).replace(/"/g, '""')}"`,
          product.image_url || "",
          product.image_path || ""
        ];
        return row.join(",");
      })
    ].join("\n");

    return csvContent;
  };

  const exportToJSON = (products) => {
    const exportData = products.map(product => ({
      id: product.id,
      name: product.name || "",
      description: product.description || "",
      price: product.price || 0,
      aisle: product.aisle || 0,
      rack_level: product.rack_level || 0,
      category_id: product.category_id || "",
      category_name: getCategoryName(product.category_id),
      barcode: product.barcode || "",
      keywords: Array.isArray(product.keywords) ? product.keywords : [],
      image_url: product.image_url || "",
      image_path: product.image_path || ""
    }));

    return JSON.stringify(exportData, null, 2);
  };

  const downloadFile = (content, filename, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setLoading(true);
    setError("");
    setExportResults(null);

    try {
      // Fetch all products from database
      const productsSnapshot = await getDocs(collection(db, "products"));
      const products = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (products.length === 0) {
        setError("No products found to export");
        return;
      }

      let content, filename, contentType;

      if (exportFormat === "csv") {
        content = exportToCSV(products);
        filename = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
        contentType = "text/csv;charset=utf-8;";
      } else {
        content = exportToJSON(products);
        filename = `products_export_${new Date().toISOString().split('T')[0]}.json`;
        contentType = "application/json;charset=utf-8;";
      }

      downloadFile(content, filename, contentType);

      setExportResults({
        totalProducts: products.length,
        format: exportFormat.toUpperCase(),
        filename: filename
      });

    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setExportResults(null);
    setExportFormat("csv");
    onClose();
  };

  const handleFormatChange = (event) => {
    setExportFormat(event.target.value);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Download />
        Export Products
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Export all products from your database with all related data fields. Choose your preferred format below.
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Export Format</FormLabel>
            <RadioGroup
              value={exportFormat}
              onChange={handleFormatChange}
            >
              <FormControlLabel
                value="csv"
                control={<Radio />}
                label="CSV (Comma Separated Values) - Compatible with Excel"
              />
              <FormControlLabel
                value="json"
                control={<Radio />}
                label="JSON (JavaScript Object Notation) - For developers"
              />
            </RadioGroup>
          </FormControl>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>Exported fields include:</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "0.875rem" }}>
            • Product ID, Name, Description<br/>
            • Price, Aisle, Rack Level<br/>
            • Category ID and Category Name<br/>
            • Barcode and Keywords<br/>
            • Image URL and Image Path
          </Typography>

          {exportFormat === "csv" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                CSV files can be opened in Excel, Google Sheets, or imported back into the system.
                Keywords will be separated by semicolons (;).
              </Typography>
            </Alert>
          )}

          {exportFormat === "json" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                JSON format preserves data types and array structures.
                Ideal for data backup or integration with other systems.
              </Typography>
            </Alert>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {exportResults && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Export Successful!</Typography>
            <Typography variant="body2">
              • Exported {exportResults.totalProducts} products
            </Typography>
            <Typography variant="body2">
              • Format: {exportResults.format}
            </Typography>
            <Typography variant="body2">
              • File: {exportResults.filename}
            </Typography>
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography>
              Exporting products...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {exportResults ? "Close" : "Cancel"}
        </Button>

        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          startIcon={<FileDownload />}
          disabled={loading}
        >
          {loading ? "Exporting..." : `Export as ${exportFormat.toUpperCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProductExport;